import { useCallback, useEffect, useState } from "react";
import config from "@/config";
import type {
  StructuredOutputRegressionCaseType,
  StructuredOutputRegressionConfig,
} from "@/types/structured-output-regression";

export function useStructuredOutputRegressionConfig(
  caseType: StructuredOutputRegressionCaseType,
) {
  const [data, setData] = useState<StructuredOutputRegressionConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (type: StructuredOutputRegressionCaseType) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/structured-output-rerun/config?case_type=${type}`,
      );
      if (!res.ok) {
        let detail = "";
        try {
          const body = (await res.json()) as { detail?: unknown };
          if (typeof body.detail === "string") detail = body.detail;
        } catch {
          // ignore
        }
        throw new Error(detail || `config fetch failed: ${res.status}`);
      }
      const json = (await res.json()) as StructuredOutputRegressionConfig;
      setData(json);
      return json;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "config fetch failed";
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(caseType);
  }, [caseType, load]);

  return { data, loading, error, reload: () => load(caseType) };
}
